import { useAdminMetrics } from '../../api/admin-queries.js';
import { formatDateTime } from '../../lib/format.js';
import { ErrorState } from '../error-state.js';

type CountBy = Partial<Record<string, number>>;

const IN_PROGRESS_SUPPLY = ['PLANNED', 'SOLVING', 'REQUESTED'];
const IN_PROGRESS_PRODUCTION = ['PLANNED', 'STAGING', 'SOLVING', 'REQUESTED'];

function sum(counts: CountBy, keys?: readonly string[]): number {
  return Object.entries(counts)
    .filter(([key]) => keys === undefined || keys.includes(key))
    .reduce((total, [, value]) => total + (value ?? 0), 0);
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="rounded-card border border-border bg-card p-4">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-2xl font-bold tabular-nums">{value}</dd>
      {hint !== undefined && <dd className="text-xs text-muted-foreground">{hint}</dd>}
    </div>
  );
}

export function OperationsPanel() {
  const metrics = useAdminMetrics();
  if (metrics.isError) return <ErrorState message="No pudimos cargar el avance." />;
  if (metrics.data === undefined) return <p aria-live="polite">Cargando avance…</p>;
  const data = metrics.data;
  return (
    <section aria-labelledby="operations-title" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="operations-title" className="text-xl font-bold">
          Avance
        </h2>
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Actualizado {formatDateTime(data.generatedAt)}
        </p>
      </div>
      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Compras de insumos en curso"
          value={sum(data.supplyRequestsByStatus, IN_PROGRESS_SUPPLY)}
          hint={`${String(data.supplyRequestsByStatus.RECEIVED ?? 0)} recibidas en total`}
        />
        <Stat
          label="Órdenes de producción en curso"
          value={sum(data.productionOrdersByStatus, IN_PROGRESS_PRODUCTION)}
          hint={`${String(data.productionOrdersByStatus.PRODUCED ?? 0)} terminadas · ${String(data.productionOrdersByStatus.FAILED ?? 0)} fallidas`}
        />
        <Stat
          label="Unidades vencidas"
          value={sum(data.expiredBySku)}
          hint="Insumos, intermedios y kits"
        />
        <Stat
          label="Llamadas a Farma Central (1 h)"
          value={data.farmaCentralLastHour.calls}
          hint={`${String(data.farmaCentralLastHour.errors)} con error`}
        />
      </dl>
    </section>
  );
}
